import { useCallback } from 'react';

import { useUpdateConsignmentWithAttachmentsMutation } from '../api/consignmentsApi';
import type { ConsignmentUpdateRequest, ConsignmentUpdateParams, AttachmentType } from './types';

export interface UpdateConsignmentWithAttachmentsData {
  files?: File[];
  attachmentType?: AttachmentType;
  comment?: string;
}

export interface UseUpdateConsignmentWithAttachmentsOptions {
  onSuccess?: (data: unknown) => void;
  onError?: (error: unknown) => void;
}

export const useUpdateConsignmentWithAttachments = (options?: UseUpdateConsignmentWithAttachmentsOptions) => {
  const [updateConsignmentWithAttachments, { isLoading, isSuccess, isError, error, data }] =
    useUpdateConsignmentWithAttachmentsMutation();

  const handleUpdate = useCallback(
    async (
      params: ConsignmentUpdateParams,
      data: ConsignmentUpdateRequest,
      attachmentsData?: UpdateConsignmentWithAttachmentsData
    ) => {
      try {
        const response = await updateConsignmentWithAttachments({
          ...params,
          ...data,
          // Files are optional - pass empty array if none
          files: attachmentsData?.files || [],
          // Default to 'OT' (Other) if no specific type
          attachmentType: attachmentsData?.attachmentType || 'OT',
          comment: attachmentsData?.comment
        }).unwrap();

        options?.onSuccess?.(response);
        return response;
      } catch (error) {
        options?.onError?.(error);
        throw error;
      }
    },
    [updateConsignmentWithAttachments, options]
  );

  return {
    updateConsignmentWithAttachments: handleUpdate,
    isLoading,
    isSuccess,
    isError,
    error,
    data
  };
};
